import { cloneDeep, flatten } from 'lodash';
import { action, computed, observable, set } from 'mobx';
import { ICompanyType } from '~/types/server.types';
import { AtoZcomparator } from '~/utils/automation.utils';
import { matchWordStart } from '~/utils/text.utils';
import { scrollLayoutTo } from '~/utils/dom.utils';
import type { RootStore } from '~/stores/root.store';
import {
    IFlowGalleryFilter,
    IFlowGalleryTemplate,
    TMobileFilterScreen,
    TSortBy
} from '../types/flow-gallery.types';

export class FlowGalleryUiStore {
    @observable isMounted: boolean = false;
    @observable isInitialLoading: boolean = true;
    @observable isLoading: boolean = true;
    @observable sortBy: TSortBy = 'relevance-desc';
    @observable allTemplatesCount: number = null;
    @observable typeFilters: IFlowGalleryFilter[] = null;
    @observable industryFilters: IFlowGalleryFilter[] = null;
    @observable singleFilterTag: string = null;
    @observable searchQuery: string = '';
    @observable industrySearchQuery: string = '';
    @observable mobileFilterScreen: TMobileFilterScreen = null;
    @observable private _isSearchTermCommitted: boolean = false;
    @observable private _templates: IFlowGalleryTemplate[] = null;
    @observable private _prevFilterState: Partial<FlowGalleryUiStore> = null;

    rootStore: RootStore = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    @computed
    get templates() {
        return this._templates;
    }

    @computed
    get templatesCount() {
        return this.templates?.length;
    }

    getActiveFilters(filters: IFlowGalleryFilter[]) {
        const activeFilters = filters?.filter(({ isActive }) => isActive);
        return activeFilters?.length ? activeFilters : null;
    }

    @computed
    get activeTypeFilters() {
        return this.getActiveFilters(this.typeFilters);
    }

    @computed
    get activeIndustryFilters() {
        return this.getActiveFilters(this.industryFilters);
    }

    @computed
    get matchedIndustryFilters() {
        return this.industryFilters.filter(({ name }) =>
            name
                .split(' ')
                .some(part => matchWordStart(part, this.industrySearchQuery))
        );
    }

    @computed
    get activeFilterTags() {
        const filterTags: string[] = [];
        if (this.singleFilterTag) {
            filterTags.push(this.singleFilterTag);
        }
        if (this.activeTypeFilters) {
            filterTags.push(
                ...flatten(this.activeTypeFilters?.map(filter => filter.tags))
            );
        }
        return [...new Set(filterTags)];
    }

    @computed
    get activeTypeFilterKeys() {
        return this.typeFilters
            ?.filter(innerFilter => innerFilter.isActive)
            .map(activeFilter => activeFilter.key);
    }

    @computed
    get activeIndustryFilterKeys() {
        return this.industryFilters
            ?.filter(innerFilter => innerFilter.isActive)
            .map(activeFilter => activeFilter.key);
    }

    @computed
    get isMobileFilterMain() {
        return this.mobileFilterScreen === 'main';
    }

    @computed
    get isNoFilters() {
        return this.isNoCommittedFilters && !this.searchQuery;
    }

    @computed
    get isNoCommittedFilters() {
        return (
            !this.activeFilterTags.length &&
            !this.activeIndustryFilters?.length &&
            !this._isSearchTermCommitted
        );
    }

    @computed
    get isEmptyState() {
        return !this.isLoading && !this.templatesCount;
    }

    @computed
    get iSearchQueryEmpty() {
        return this.searchQuery === '';
    }

    @action.bound
    setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading;
        this._isSearchTermCommitted = !!this.searchQuery;
    }

    @action.bound
    clearInitialLoading() {
        this.isInitialLoading = false;
    }

    @action.bound
    loadTemplates(templates: IFlowGalleryTemplate[] = []) {
        this._templates = templates;

        if (this.allTemplatesCount < templates.length) {
            this.allTemplatesCount = templates.length;
        }
    }

    @action.bound
    loadTypeFilters(filters: IFlowGalleryFilter[] = []) {
        const sortedFilters = filters
            // TEMP - These filters currently have no templates associated with them in the public repo
            .filter(({ key }) => !['briefs', 'educators'].includes(key))
            .map(filter => ({ ...filter, isActive: false }))
            .sort((a, b) => AtoZcomparator(a, b, 'name'));

        const mostClickedFilters = [
            'contracts',
            'pricing_services_guides',
            'invoices',
            'brochures',
            'proposals',
            'scheduler'
        ] as const;

        mostClickedFilters.forEach((mostClickedKey, mostClickedIndex) => {
            const sortedIndex = sortedFilters.findIndex(
                sortedFilter => sortedFilter.key === mostClickedKey
            );
            if (sortedIndex > -1) {
                const [filter] = sortedFilters.splice(sortedIndex, 1);
                sortedFilters.splice(mostClickedIndex, 0, filter);
            }
        });

        this.typeFilters = sortedFilters;
    }

    @action.bound
    loadIndustryFilters(types: ICompanyType[]) {
        this.industryFilters = types.map(type => ({
            key: type.value,
            name: type.label,
            isActive: false
        }));
    }

    @action.bound
    setSortBy(sortBy: TSortBy) {
        this.sortBy = sortBy;
    }

    @action.bound
    toggleFilter(filter: { isActive?: boolean }) {
        filter.isActive = !filter.isActive;
    }

    @action.bound
    setSingleTag(tag: string) {
        this.singleFilterTag = tag;
    }

    @action.bound
    setSearchQuery(term: string) {
        this.searchQuery = term;
    }

    @action.bound
    setIndustrySearchQuery(term: string) {
        this.industrySearchQuery = term;
    }

    @action.bound
    clearAllFilters() {
        scrollLayoutTo({ top: 0, behavior: 'auto' });
        this.setSingleTag(null);
        this.setSearchQuery('');
        [this.typeFilters, this.industryFilters].forEach(list =>
            list.forEach(filter => {
                filter.isActive = false;
            })
        );
    }

    @action.bound
    setPrevFilterState() {
        const { typeFilters, industryFilters, sortBy } = this;
        this._prevFilterState = cloneDeep({
            typeFilters,
            industryFilters,
            sortBy
        });
    }

    @action.bound
    revertToPrevFilterState() {
        set(this, this._prevFilterState);
        this.clearPrevFilterState();
    }

    @action.bound
    clearPrevFilterState() {
        this._prevFilterState = null;
    }

    @action.bound
    setIsMounted(isMounted: boolean) {
        this.isMounted = isMounted;
    }

    @action.bound
    setMobileFilterScreen(screen: TMobileFilterScreen) {
        this.mobileFilterScreen = screen;
    }

    @action.bound
    openMobileFilterMainScreen() {
        this.setMobileFilterScreen('main');
    }

    @action.bound
    closeMobileFilter() {
        this.setMobileFilterScreen(null);
    }

    @action.bound
    resetFlowGallery() {
        this._templates = null;
        this.setIsMounted(false);
    }
}
